/**
 * Partners Page (UI-9)
 *
 * Partner management on BE-12: list, add, edit (TC admin only).
 * Ownership may total up to 100%; drawings and the profit share need exactly 100%.
 * Edit also deactivates a partner (its share goes to 0) and shows its ownership history.
 * Route: /accounting/partners
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, FormControlLabel, Switch, Divider,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { fontSize as fs, fontWeight as fw } from '../../utils/accounting/styles';
import { DataTable, StatusChip } from '../../components/accounting';
import { formatDate } from '../../utils/accounting/formatters';
import { toYmd } from '../../utils/accounting/periodUtils';
import { serverMessage } from '../../utils/apiConvert';
import { useNavigate } from 'react-router-dom';
import ApiService from '../../services/api.service';
import { profitShareService } from '../../services/accounting/profitShare.service';
import { partnerPeopleService } from '../../services/accounting/partnerPeople.service';
import { expenseService } from '../../services/accounting/expense.service';

const pct = (v) => `${Number(v || 0).toFixed(2)}%`;
const muted = (r) => (r.active === false ? { color: 'text.disabled' } : {});

const COLUMNS = [
  {
    key: 'name', label: 'Name', width: '16%',
    render: (r) => (
      <Typography sx={{ fontSize: fs.sm, ...muted(r) }}>
        {r.name}
        {r.active === false && <Typography component="span" sx={{ fontSize: fs.xs, ml: 1 }}>Inactive</Typography>}
      </Typography>
    ),
  },
  { key: 'ownershipPercent', label: 'Ownership %', width: '10%', align: 'right', render: (r) => <Box component="span" sx={muted(r)}>{pct(r.ownershipPercent)}</Box> },
  { key: 'role', label: 'Role', width: '8%', render: (r) => <StatusChip type="partnerRole" value={r.role} /> },
  {
    key: 'person', label: 'Person', width: '18%',
    render: (r) => (r.person
      ? `${r.person.fullName} · login ${r.person.loginEnabled ? '✓' : '—'}${r.person.roleName || r.person.userRole ? ` · ${r.person.roleName || r.person.userRole}` : ''}`
      : 'Not linked'),
  },
  { key: 'email', label: 'Email', width: '13%', render: (r) => r.email || '—' },
  { key: 'phone', label: 'Phone', width: '9%', render: (r) => r.phone || '—' },
  { key: 'effectiveDate', label: 'Effective Date', width: '10%', render: (r) => formatDate(r.effectiveDate) },
];

const initialForm = () => ({
  name: '',
  ownershipPercent: '',
  role: 'ACTIVE',
  phone: '',
  email: '',
  effectiveDate: toYmd(new Date()),
  active: true,
});

const Partners = ({ currentUser }) => {
  const navigate = useNavigate();
  const canEdit = currentUser?.role === 'TC_ADMIN';
  const [busyId, setBusyId] = useState(null);
  const [notice, setNotice] = useState(null);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState(null);
  const [members, setMembers] = useState([]);
  const [personId, setPersonId] = useState('');

  const loadPartners = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await profitShareService.getPartners();
      setPartners(res?.data || []);
    } catch (err) {
      setError(serverMessage(err, 'Failed to load partners'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPartners(); }, [loadPartners]);

  useEffect(() => {
    if (canEdit) expenseService.getTeamMembers(currentUser?.typingCenterId).then(setMembers).catch(() => setMembers([]));
  }, [canEdit, currentUser?.typingCenterId]);

  // A partner without a person becomes a team member without sign-in, linked at once.
  const addAsMember = async (partner) => {
    setBusyId(partner.id);
    setError(null);
    try {
      const res = await ApiService.createTeamMember({
        fullName: partner.name, loginEnabled: false, role: 'TC_STAFF', role_id: null, typingCenterId: currentUser?.typingCenterId,
      });
      await partnerPeopleService.link(partner.id, res?.data?.data?.id);
      setNotice(`${partner.name} added to the team and linked`);
      loadPartners();
    } catch (err) {
      setError(serverMessage(err, 'Could not add the partner as a team member'));
    } finally {
      setBusyId(null);
    }
  };

  const columns = canEdit ? [...COLUMNS, {
    key: 'actions', label: '', width: '16%', align: 'right',
    render: (r) => {
      if (!r.person) {
        return (
          <Button size="small" disabled={busyId === r.id} sx={{ textTransform: 'none', fontSize: fs.xs }}
            onClick={(e) => { e.stopPropagation(); addAsMember(r); }}>Add as team member</Button>
        );
      }
      if (!r.person.loginEnabled) {
        return (
          <Button size="small" sx={{ textTransform: 'none', fontSize: fs.xs }}
            onClick={(e) => { e.stopPropagation(); navigate(`/team?edit=${r.userId}&access=1`); }}>Give access</Button>
        );
      }
      return null;
    },
  }] : COLUMNS;

  // Active partners' ownership; the one being edited counts with the form's value.
  const activeTotal = partners.reduce((s, p) => (p.active === false ? s : s + Number(p.ownershipPercent || 0)), 0);
  const othersTotal = partners.reduce((s, p) => {
    if (p.active === false || (editing && p.id === editing.id)) return s;
    return s + Number(p.ownershipPercent || 0);
  }, 0);
  const newTotal = othersTotal + (form.active ? Number(form.ownershipPercent) || 0 : 0);
  const overHundred = newTotal > 100.005;
  const totalHelp = !form.ownershipPercent || !form.active
    ? null
    : overHundred
      ? `Total would be ${newTotal.toFixed(2)}% — partners can hold at most 100%`
      : Math.abs(newTotal - 100) < 0.01
        ? 'Total ownership will be 100%'
        : `Total will be ${newTotal.toFixed(2)}% — drawings need 100%`;

  const openAdd = () => {
    setEditing(null);
    setForm(initialForm());
    setPersonId('');
    setDialogError(null);
    setDialogOpen(true);
  };

  const openEdit = (partner) => {
    setEditing(partner);
    setForm({
      name: partner.name,
      ownershipPercent: partner.active === false ? '' : Number(partner.ownershipPercent).toFixed(2),
      role: partner.role,
      phone: partner.phone || '',
      email: partner.email || '',
      effectiveDate: toYmd(new Date()),
      active: partner.active !== false,
    });
    setPersonId(partner.userId || '');
    setDialogError(null);
    setDialogOpen(true);
  };

  // Edit sends only what changed; the effective date goes with an ownership or active change.
  const changes = () => {
    const out = {};
    if (form.name.trim() !== editing.name) out.name = form.name.trim();
    if (form.role !== editing.role) out.role = form.role;
    if (form.phone !== (editing.phone || '')) out.phone = form.phone;
    if (form.email !== (editing.email || '')) out.email = form.email;
    const wasActive = editing.active !== false;
    if (!form.active && wasActive) out.active = false;
    if (form.active && !wasActive) out.active = true;
    if (form.active && Number(form.ownershipPercent) !== Number(wasActive ? editing.ownershipPercent : 0)) {
      out.ownershipPercent = form.ownershipPercent;
    }
    if ('active' in out || 'ownershipPercent' in out) out.effectiveDate = form.effectiveDate;
    return out;
  };

  const handleSubmit = async () => {
    setSaving(true);
    setDialogError(null);
    try {
      if (editing) {
        const body = changes();
        if (Object.keys(body).length) await profitShareService.updatePartner(editing.id, body);
        if (personId !== (editing.userId || '')) {
          if (personId) await partnerPeopleService.link(editing.id, personId);
          else await partnerPeopleService.unlink(editing.id);
        }
      } else {
        const { active, ...body } = form;
        await profitShareService.createPartner({ ...body, name: body.name.trim() });
      }
      setDialogOpen(false);
      setEditing(null);
      loadPartners();
    } catch (err) {
      setDialogError(serverMessage(err, 'Failed to save partner'));
    } finally {
      setSaving(false);
    }
  };

  const history = editing?.history || [];

  return (
    <Box sx={{ p: 2, width: 0, minWidth: '100%', boxSizing: 'border-box' }}>
      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setNotice(null)}>{notice}</Alert>}

      <Typography variant="h6" sx={{ fontSize: fs.md, fontWeight: fw.semibold, mb: 1 }}>
        Partners
      </Typography>

      {!loading && partners.length > 0 && Math.abs(activeTotal - 100) >= 0.01 && (
        <Alert severity="warning" sx={{ mb: 1, py: 0, fontSize: fs.sm }}>
          Ownership totals {activeTotal.toFixed(2)}% — drawings and the profit share need 100%.
        </Alert>
      )}

      {/* Header row */}
      {canEdit && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<Add />}
            onClick={openAdd}
            sx={{ textTransform: 'none', fontSize: fs.sm }}
          >
            Add Partner
          </Button>
        </Box>
      )}

      {/* Data table */}
      <DataTable
        columns={columns}
        data={partners}
        loading={loading}
        onRowClick={canEdit ? openEdit : undefined}
        emptyMessage="No partners configured."
        emptyAction={canEdit ? { label: 'Add your first partner', onClick: openAdd } : undefined}
      />

      {/* Add/Edit Partner Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: fs.md, fontWeight: fw.semibold }}>
          {editing ? 'Edit Partner' : 'Add Partner'}
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          {dialogError && <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              size="small"
              fullWidth
              autoFocus
            />
            {editing && (
              <FormControlLabel
                sx={{ mt: -1, mb: -1 }}
                control={<Switch size="small" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />}
                label={(
                  <Typography sx={{ fontSize: fs.sm }}>
                    {form.active ? 'Active partner' : 'Inactive — holds no ownership from the effective date'}
                  </Typography>
                )}
              />
            )}
            <TextField
              label="Ownership %"
              type="number"
              value={form.active ? form.ownershipPercent : ''}
              onChange={(e) => setForm({ ...form, ownershipPercent: e.target.value })}
              size="small"
              fullWidth
              disabled={!form.active}
              inputProps={{ min: 0, max: 100, step: '0.01' }}
              helperText={totalHelp}
              error={Boolean(form.ownershipPercent) && form.active && overHundred}
            />
            <TextField
              select
              label="Role"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              size="small"
              fullWidth
            >
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="SILENT">Silent</MenuItem>
            </TextField>
            <TextField
              label="Phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              size="small"
              fullWidth
            />
            <TextField
              label="Email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              size="small"
              fullWidth
            />
            <TextField
              label="Effective Date"
              type="date"
              value={form.effectiveDate}
              onChange={(e) => setForm({ ...form, effectiveDate: e.target.value })}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: toYmd(new Date()) }}
              helperText={editing ? 'From when a new ownership % or active change applies' : null}
            />
            {editing && (
              <TextField
                select
                label="Person (team member)"
                value={personId}
                onChange={(e) => setPersonId(e.target.value)}
                size="small"
                fullWidth
                helperText="Links this partner to a member of your team (sign-in optional). A member can be linked to one partner."
              >
                <MenuItem value="">Not linked</MenuItem>
                {members
                  .filter((m) => m.id === editing.userId || !partners.some((p) => p.userId === m.id))
                  .map((m) => <MenuItem key={m.id} value={m.id}>{m.fullName || m.username}</MenuItem>)}
              </TextField>
            )}
            {history.length > 0 && (
              <Box>
                <Divider sx={{ mb: 1 }} />
                <Typography sx={{ fontSize: fs.xs, fontWeight: fw.semibold, color: 'text.secondary', textTransform: 'uppercase', mb: 0.5 }}>
                  Ownership history
                </Typography>
                {history.map((h, i) => (
                  <Box key={`${h.effectiveDate}-${i}`} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
                    <Typography sx={{ fontSize: fs.sm }}>From {formatDate(h.effectiveDate)}</Typography>
                    <Typography sx={{ fontSize: fs.sm, fontWeight: i === 0 ? fw.semibold : fw.normal }}>{pct(h.ownershipPercent)}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving || !form.name.trim() || (form.active && !form.ownershipPercent) || (form.active && overHundred)}
            sx={{ textTransform: 'none' }}
          >
            {saving ? 'Saving...' : editing ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Partners;
